import { type OnboardingFlowType } from '#app/components/GetStartedPage/types.js';
import { getOnboardingFlowType } from '#app/components/GetStartedPage/utils/getOnboardingFlowType.js';
import { type AdditionalTrackingProperties } from '#app/components/Modals/V2/GlobalModalSystem/types.js';
import { useModal } from '#app/components/Modals/V2/GlobalModalSystem/useModal.js';
import { ONBOARDING_COMPLETED_URL_PARAMETER } from '#app/components/OnboardingSuccess/constants.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useURLParameters } from '#app/hooks/useURLParameters.js';
import { appendSearch } from '#app/utilities/appendSearch.js';
import { shouldRedirectToOnboarding } from '#app/utilities/shouldRedirectToOnboarding.js';
import { getRoute } from '@contra/routes';
import { useCallback, useMemo } from 'react';

type useOnboardingRedirectionProps = {
  onClientModalClose?: () => void;
  /**
   * Overrides the onboarding user type inferred from the user's sign up type, or the userType search parameter.
   * This will change the copy shown in the redirection modal, and also auto-start the onboarding flow with that user type (via the userType search param).
   */
  overrideUserType?: OnboardingFlowType;
  /**
   * The route to redirect to after completing onboarding.
   */
  redirectTo?: string;
};

type useOnboardingRedirectionReturn = {
  handleOnboardingRedirectionModalOpen: (
    additionalTrackingProperties?: AdditionalTrackingProperties,
  ) => void;
  shouldRedirectUser: boolean;
  signUpPath: string;
};

export const useOnboardingRedirection = ({
  onClientModalClose,
  overrideUserType,
  redirectTo,
}: useOnboardingRedirectionProps): useOnboardingRedirectionReturn => {
  const currentUser = useCurrentUser();
  const [urlSearchParameters] = useURLParameters('*');
  const userTypeSearchParameter = urlSearchParameters.userType;

  const hasSeenWelcomeConfetti =
    currentUser?.userInterfaceFlags?.includes(
      'HIDE_FTUE_ONBOARDING_COMPLETED_MODAL',
    ) ?? false;

  const inferredOnboardingFlowType: OnboardingFlowType | undefined =
    currentUser?.profile?.onboardingType === undefined
      ? undefined
      : getOnboardingFlowType(
          currentUser.profile.onboardingType,
          userTypeSearchParameter,
        );
  const onboardingFlowType: OnboardingFlowType | undefined =
    overrideUserType ?? inferredOnboardingFlowType;

  const shouldRedirectToSignUp = currentUser === null;
  const shouldRedirectToOnboardingFlow =
    currentUser !== null &&
    shouldRedirectToOnboarding(currentUser.userGroups, onboardingFlowType);

  const redirectionRoute = shouldRedirectToOnboardingFlow
    ? getRoute('/get-started', {
        search: {
          userType: onboardingFlowType,
        },
      })
    : getRoute('/sign-up', {
        search: {
          userType: onboardingFlowType,
        },
      });

  const redirectToWithMaybeWelcomeConfetti =
    redirectTo === undefined
      ? undefined
      : hasSeenWelcomeConfetti
        ? redirectTo
        : appendSearch(redirectTo, {
            [ONBOARDING_COMPLETED_URL_PARAMETER]: true,
          });

  const completeSignUpPath = appendSearch(redirectionRoute, {
    redirectTo: redirectToWithMaybeWelcomeConfetti,
  });

  const { openModal: openClientModal } = useModal({
    identifier: 'ClientOnboardingRedirectionModal',
    modalProps: {
      completeSignUpPath: completeSignUpPath ?? null,
      onModalClose: onClientModalClose,
    },
  });
  const { openModal: openIndyModal } = useModal({
    identifier: 'IndyOnboardingRedirectionModal',
    modalProps: {
      completeSignUpPath: completeSignUpPath ?? null,
    },
  });

  const handleModalOpen = useCallback(
    (additionalTrackingProperties?: AdditionalTrackingProperties) => {
      if (onboardingFlowType === 'client') {
        openClientModal(additionalTrackingProperties);
      } else {
        openIndyModal(additionalTrackingProperties);
      }
    },
    [onboardingFlowType, openClientModal, openIndyModal],
  );

  return useMemo(() => {
    return {
      handleOnboardingRedirectionModalOpen: handleModalOpen,
      shouldRedirectUser:
        shouldRedirectToOnboardingFlow || shouldRedirectToSignUp,
      signUpPath: completeSignUpPath,
    };
  }, [
    completeSignUpPath,
    handleModalOpen,
    shouldRedirectToOnboardingFlow,
    shouldRedirectToSignUp,
  ]);
};
